import { useAuth } from "@/shared/providers/AuthProvider";
import { useTenantContext } from "@/shared/providers/TenantProvider";
import { PERMISSIONS } from "@/shared/constants/permissions";
import { PermissionService } from "@/shared/services/permission.service";
import { FeatureToggleService } from "@/shared/services/featureToggle.service";

export const usePermissions = () => {
  const { user } = useAuth();
  const { tenant } = useTenantContext();
  const permissionService = PermissionService.getInstance();
  const featureService = FeatureToggleService.getInstance();

  /**
   * Check if user has a single permission (direct API format)
   * @param permission - Permission string from PERMISSIONS (e.g., PERMISSIONS.VEHICLE_CREATE)
   * @returns true if user has the permission
   */
  const hasPermission = (permission: string, context?: any): boolean => {
    if (!user) return false;
    return permissionService.hasPermission(user, permission, context);
  };

  /**
   * Check multiple permissions with AND/OR logic
   * @param permissions - Array of permission strings from PERMISSIONS
   * @param requireAll - If true, user must have ALL permissions (AND logic). If false, user needs ANY permission (OR logic)
   * @returns true if permission check passes
   */
  const hasPermissions = (permissions: string[], requireAll: boolean = false): boolean => {
    if (!user || !permissions || permissions.length === 0) return false;
    
    const userPermissions = permissionService.getUserPermissions(user);
    
    if (requireAll) {
      // AND logic - user must have ALL permissions
      return permissions.every(perm => userPermissions.includes(perm));
    } else {
      // OR logic - user must have ANY permission
      return permissions.some(perm => userPermissions.includes(perm));
    }
  };

  // Check if user has specific role
  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return permissionService.hasRole(user, role);
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles: string[]): boolean => {
    if (!user) return false;
    return permissionService.hasAnyRole(user, roles);
  };

  // Check if feature is enabled for tenant AND user has required role/permission
  const canAccessFeature = (
    featureKey: string,
    requiredPermission?: string,
    requiredRoles?: string[],
  ): boolean => {
    if (!user || !tenant) return false;

    // Check feature toggle
    const featureResult = featureService.isFeatureEnabled(
      featureKey,
      user,
      tenant,
    );
    if (!featureResult.enabled) {
      return false;
    }

    // Check permission if specified
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return false;
    }

    // Check roles if specified
    if (requiredRoles && !permissionService.hasAnyRole(user, requiredRoles)) {
      return false;
    }

    return true;
  };

  // Get all enabled features for current user (considering both feature flags and permissions)
  const getEnabledFeatures = () => {
    if (!user || !tenant) return [];

    const features = [];

    if (
      canAccessFeature("scheduling", PERMISSIONS.METAROUTE_VIEW, [
        "scheduler",
        "dispatcher",
        "admin",
      ])
    ) {
      features.push("scheduling");
    }

    if (
      canAccessFeature("realTimeTracking", PERMISSIONS.VEHICLE_VIEW, [
        "dispatcher",
        "admin",
        "supervisor",
      ])
    ) {
      features.push("realTimeTracking");
    }

    if (
      canAccessFeature("reporting", PERMISSIONS.REPORTING_ACCESS, ["admin", "supervisor"])
    ) {
      features.push("reporting");
    }

    return features;
  };

  // Get user's effective permissions (including inherited)
  const getUserPermissions = () => {
    if (!user) return [];
    return permissionService.getUserPermissions(user);
  };

  // Get user's effective roles (including inherited)
  const getUserRoles = () => {
    if (!user) return [];
    return permissionService.getEffectiveRoles(user);
  };

  return {
    user,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasPermissions,
    canAccessFeature,
    getEnabledFeatures,
    getUserPermissions,
    getUserRoles,
  };
};
